import { useCreate } from '@refinedev/core';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { REGIONS, type Region } from '@/lib/types';

export function TopupCreatePage() {
  const navigate = useNavigate();
  const { mutate: create, isLoading } = useCreate();

  const [region, setRegion] = useState<Region>('BR');
  const [currency, setCurrency] = useState('BRL');
  const [faceValue, setFaceValue] = useState('');
  const [costBrl, setCostBrl] = useState('');
  const [code, setCode] = useState('');
  const [provider, setProvider] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fv = parseFloat(faceValue);
    const cb = parseFloat(costBrl);
    if (!fv || fv <= 0) {
      toast.error('Valor face inválido.');
      return;
    }
    if (isNaN(cb) || cb < 0) {
      toast.error('Custo BRL inválido.');
      return;
    }
    if (!code.trim()) {
      toast.error('Código do gift card obrigatório.');
      return;
    }
    if (currency.length !== 3) {
      toast.error('Currency deve ter 3 letras (ISO 4217). Ex: BRL, USD, EUR.');
      return;
    }

    create(
      {
        resource: 'topup',
        values: {
          region,
          currency: currency.toUpperCase(),
          face_value: fv,
          cost_brl: cb,
          code: code.trim(),
          provider: provider.trim() || undefined,
          expires_at: expiresAt ? new Date(expiresAt).toISOString() : undefined,
          notes: notes.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success('Gift card registrado.');
          navigate('/topup');
        },
        onError: () => toast.error('Falha ao criar gift card.'),
      },
    );
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2">
          <Link to="/topup">← Voltar</Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Novo gift card</h1>
        <p className="text-muted-foreground">
          Registre um código comprado externamente. O código fica encriptado em repouso.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dados do gift card</CardTitle>
          <CardDescription>
            Region/currency da PSN Store. Custo BRL = quanto você efetivamente pagou em reais.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="region">Região *</Label>
                <Select value={region} onValueChange={(v) => setRegion(v as Region)}>
                  <SelectTrigger id="region">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency (ISO 4217) *</Label>
                <Input
                  id="currency"
                  maxLength={3}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  placeholder="BRL"
                  className="font-mono uppercase"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="face_value">Valor face *</Label>
                <Input
                  id="face_value"
                  type="number"
                  step="0.01"
                  min="0"
                  value={faceValue}
                  onChange={(e) => setFaceValue(e.target.value)}
                  placeholder="50.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cost_brl">Custo BRL *</Label>
                <Input
                  id="cost_brl"
                  type="number"
                  step="0.01"
                  min="0"
                  value={costBrl}
                  onChange={(e) => setCostBrl(e.target.value)}
                  placeholder="40.00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">Código *</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                spellCheck={false}
                autoComplete="off"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Encriptado em repouso (AES-256-GCM). Visível só via reveal flow autorizado.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="provider">Provider</Label>
                <Input
                  id="provider"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  placeholder="eneba / ggsel / seagm / manual"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expires_at">Expira em</Label>
                <Input
                  id="expires_at"
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" asChild>
                <Link to="/topup">Cancelar</Link>
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Registrar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
